import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Header from "@/components/Header";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  ChevronRight,
  ChevronLeft,
  Check,
  Sparkles,
  TrendingUp,
  Home,
  Lightbulb,
  Cog,
  Code,
  GraduationCap,
  Megaphone,
  DollarSign,
  Image as ImageIcon,
  Zap,
  Clock,
  Flame,
} from "lucide-react";
import { toast } from "sonner";
import { capture } from "@/lib/analytics";

const ICON_MAP = {
  TrendingUp, Home, Lightbulb, Cog, Code, GraduationCap, Megaphone,
  DollarSign, Image: ImageIcon, Zap,
};

const TIMES = [
  { value: 5,  label: "5 min/day",  sub: "Quick reps, real progress" },
  { value: 10, label: "10 min/day", sub: "The sweet spot" },
  { value: 30, label: "30 min/day", sub: "Belt up fast" },
];

const MAX_FOCUSES = 3;

export default function Onboarding() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [focuses, setFocuses] = useState([]); // array of focus slugs
  const [allFocuses, setAllFocuses] = useState([]);
  const [timePerDay, setTimePerDay] = useState(null);
  const [timeError, setTimeError] = useState(false);
  const [busy, setBusy] = useState(false);

  // Hydrate persisted time_per_day from the server (return-to-onboarding flow)
  useEffect(() => {
    if (user?.time_per_day && timePerDay === null) {
      setTimePerDay(user.time_per_day);
    }
  }, [user?.time_per_day, timePerDay]);

  useEffect(() => {
    if (user?.onboarding_completed_at) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    (async () => {
      try {
        const r = await apiClient.get("/seo/focuses");
        setAllFocuses(r.data?.focuses || []);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[onboarding] /seo/focuses fetch failed:", e?.message);
      }
    })();
    // Pull in selections from the Landing identity builder, if any
    try {
      const raw = localStorage.getItem("pending_focuses");
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr) && arr.length > 0) {
          setFocuses(arr.slice(0, MAX_FOCUSES));
          // If user already picked 3 on landing, skip step 0 → go straight to recap
          if (arr.length >= MAX_FOCUSES) setStep(1);
        }
        localStorage.removeItem("pending_focuses");
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[onboarding] could not read pending_focuses:", e?.message);
    }
  }, []);

  const toggleFocus = (slug) => {
    setFocuses((cur) => {
      if (cur.includes(slug)) return cur.filter((s) => s !== slug);
      if (cur.length >= MAX_FOCUSES) {
        toast.error(`Pick up to ${MAX_FOCUSES} focuses. Tap one to swap.`);
        return cur;
      }
      return [...cur, slug];
    });
  };

  const selectedFocusObjects = focuses
    .map((slug) => allFocuses.find((f) => f.slug === slug))
    .filter(Boolean);

  const canAdvance =
    (step === 0 && focuses.length >= 1) ||
    (step === 1) ||
    (step === 2 && timePerDay !== null);

  const submit = async () => {
    if (timePerDay === null) {
      setTimeError(true);
      return;
    }
    setBusy(true);
    try {
      let tz = null;
      let locale = null;
      let language_code = null;
      try {
        tz = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[onboarding] timezone detection failed:", e?.message);
        tz = null;
      }
      try {
        locale = navigator.language || null;
        language_code = locale ? locale.split("-")[0].toLowerCase() : null;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[onboarding] navigator.language read failed:", e?.message);
      }
      // Country/currency: ask the backend's /profile/geo (CF-IPCountry header).
      let country = null;
      let currency = null;
      try {
        const g = await apiClient.get("/profile/geo");
        country = g.data?.country || null;
        currency = g.data?.currency || null;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[onboarding] /profile/geo lookup failed — backend will default to USD:", e?.message);
      }
      await apiClient.post("/onboarding/complete", {
        focuses,
        time_per_day: timePerDay,
        timezone: tz,
        locale,
        language_code,
        country,
        currency,
      });
      capture("onboarding_completed", {
        focuses,
        time_per_day: timePerDay,
        timezone: tz,
        country,
        currency,
        language_code,
      });
      await refresh?.();
      toast.success("Your dojo is set up. Let's go.");
      navigate("/dashboard", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save your choices");
    } finally {
      setBusy(false);
    }
  };

  const skip = async () => {
    try {
      await apiClient.post("/onboarding/skip");
      capture("onboarding_skipped");
      await refresh?.();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[onboarding] skip failed (navigating anyway):", e?.message);
    }
    navigate("/dashboard", { replace: true });
  };

  const firstName = (user?.name || "").split(" ")[0] || "Friend";

  return (
    <div className="App min-h-screen">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14 pb-28 md:pb-14">
        {/* Progress bar */}
        <div className="flex items-center gap-2 mb-8" data-testid="onboarding-progress">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="flex-1 h-1.5 rounded-full transition-colors"
              style={{ background: i <= step ? "var(--emerald, #10B981)" : "var(--line, #E5E7EB)" }}
            />
          ))}
        </div>

        <span className="kicker">Set up your dojo · {step + 1} / 3</span>

        <AnimatePresence mode="wait">
          {/* STEP 0 — Choose Your AI Focus */}
          {step === 0 && (
            <motion.div
              key="step-focus"
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -12 }}
              transition={{ duration: 0.25 }}
              data-testid="onboarding-step-focus"
            >
              <h1 className="font-display text-4xl md:text-5xl mt-3 leading-[1.05] tracking-tight">
                Choose your AI focus.
              </h1>
              <p className="text-[var(--ink-2)] mt-3 leading-relaxed max-w-lg">
                Select up to <strong>3 areas</strong> you want AI to help you master. Your daily challenges adapt to what you pick.
              </p>
              <div className="mt-5 mb-2 text-xs uppercase tracking-[0.18em] text-[var(--ink-3)] font-medium">
                {focuses.length} / {MAX_FOCUSES} selected
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                {allFocuses.map((f) => {
                  const Icon = ICON_MAP[f.icon] || Sparkles;
                  const active = focuses.includes(f.slug);
                  return (
                    <button
                      key={f.slug}
                      onClick={() => toggleFocus(f.slug)}
                      className={`brut p-4 md:p-5 text-left transition-all relative overflow-hidden ${active ? "ring-2 ring-[var(--emerald,#10B981)] -translate-y-0.5" : "hover:-translate-y-0.5"}`}
                      style={active ? { background: `linear-gradient(135deg, ${f.accent}10 0%, ${f.accent}05 100%)` } : {}}
                      data-testid={`onboarding-focus-${f.slug}`}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                          style={{ background: f.accent }}
                        >
                          <Icon size={20} color="#fff" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xl leading-none">{f.emoji}</span>
                            <span className="font-display text-base md:text-lg leading-tight">{f.name}</span>
                          </div>
                          <ul className="mt-2 text-xs text-[var(--ink-2)] leading-snug space-y-0.5">
                            {f.bullets.map((b) => (
                              <li key={b}>• {b}</li>
                            ))}
                          </ul>
                        </div>
                        {active && (
                          <div
                            className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{ background: "var(--emerald, #10B981)" }}
                            aria-label="Selected"
                          >
                            <Check size={13} color="#fff" />
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* STEP 1 — Identity recap */}
          {step === 1 && (
            <motion.div
              key="step-recap"
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -12 }}
              transition={{ duration: 0.25 }}
              data-testid="onboarding-step-recap"
            >
              <h1 className="font-display text-4xl md:text-5xl mt-3 leading-[1.05] tracking-tight">
                {firstName}'s AI Journey
              </h1>
              <p className="text-[var(--ink-2)] mt-3 leading-relaxed max-w-lg">
                Your personalized path. Every daily challenge is hand-picked from these focuses.
              </p>
              <div className="kicker mt-7 mb-3">Primary focuses</div>
              <div className="space-y-3" data-testid="onboarding-recap-list">
                {selectedFocusObjects.map((f, i) => {
                  const Icon = ICON_MAP[f.icon] || Sparkles;
                  return (
                    <div
                      key={f.slug}
                      className="brut p-4 flex items-center gap-3"
                      style={{ background: `linear-gradient(90deg, ${f.accent}0d 0%, transparent 100%)` }}
                    >
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ background: f.accent }}
                      >
                        <Icon size={18} color="#fff" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-display text-lg leading-tight">{f.name}</div>
                        <div className="text-xs text-[var(--ink-2)] mt-0.5">{f.tagline}</div>
                      </div>
                      <span
                        className="kicker"
                        style={{ color: f.accent, opacity: 0.9 }}
                      >
                        #{i + 1}
                      </span>
                    </div>
                  );
                })}
              </div>
              {/* Motivational social proof */}
              <div
                className="mt-7 p-4 rounded-2xl flex items-start gap-3"
                style={{
                  background: "linear-gradient(135deg, rgba(245,158,11,0.08) 0%, rgba(245,158,11,0.03) 100%)",
                  border: "1px solid rgba(245,158,11,0.22)",
                }}
              >
                <Flame size={18} className="mt-0.5 flex-shrink-0" style={{ color: "var(--streak, #F59E0B)" }} />
                <div className="text-sm leading-relaxed text-[var(--ink)]">
                  <strong>You're ahead of 68% of users</strong> who pick a focus on day one. Now pick a daily pace and lock the streak in.
                </div>
              </div>
            </motion.div>
          )}

          {/* STEP 2 — Time per day */}
          {step === 2 && (
            <motion.div
              key="step-time"
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -12 }}
              transition={{ duration: 0.25 }}
              data-testid="onboarding-step-time"
            >
              <h1 className="font-display text-4xl md:text-5xl mt-3 leading-[1.05] tracking-tight">
                How much time per day?
              </h1>
              <p className="text-[var(--ink-2)] mt-3 leading-relaxed max-w-lg">
                Honest answers win. We'll tune your daily lesson load. Maintain the streak to preserve progress.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6">
                {TIMES.map((t) => {
                  const active = timePerDay === t.value;
                  return (
                    <button
                      key={t.value}
                      onClick={() => { setTimePerDay(t.value); setTimeError(false); }}
                      className={`brut p-5 text-left transition-all ${active ? "ring-2 ring-[var(--emerald,#10B981)] -translate-y-0.5" : "hover:-translate-y-0.5"}`}
                      data-testid={`onboarding-time-${t.value}`}
                      aria-pressed={active}
                    >
                      <Clock size={16} className="text-[var(--ink-3)] mb-2" />
                      <div className="font-display text-2xl leading-tight">{t.label}</div>
                      <div className="text-xs text-[var(--ink-2)] mt-1">{t.sub}</div>
                    </button>
                  );
                })}
              </div>
              {timeError && (
                <div
                  className="mt-4 text-sm text-[var(--accent,#FF5C2A)]"
                  data-testid="onboarding-time-error"
                  role="alert"
                >
                  Select your daily time commitment to continue.
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Nav controls */}
        <div className="mt-10 flex items-center justify-between gap-3 flex-wrap">
          <button
            onClick={skip}
            className="text-sm text-[var(--ink-3)] hover:text-[var(--ink-2)]"
            data-testid="onboarding-skip"
          >
            Skip for now
          </button>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <button
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                className="btn-ghost"
                style={{ padding: "0.55rem 1rem", fontSize: "0.85rem" }}
                data-testid="onboarding-back"
              >
                <ChevronLeft size={14} /> Back
              </button>
            )}
            {step < 2 ? (
              <button
                onClick={() => canAdvance && setStep((s) => s + 1)}
                disabled={!canAdvance}
                className="btn-primary"
                style={{ padding: "0.55rem 1rem", fontSize: "0.85rem" }}
                data-testid="onboarding-next"
              >
                Next <ChevronRight size={14} />
              </button>
            ) : (
              <button
                onClick={() => {
                  if (timePerDay === null) { setTimeError(true); return; }
                  submit();
                }}
                disabled={busy}
                className="btn-duo"
                style={{ padding: "0.6rem 1.2rem", fontSize: "0.9rem", opacity: timePerDay === null ? 0.6 : 1 }}
                data-testid="onboarding-finish"
              >
                {busy ? <span className="spinner" /> : <>Start my journey <ChevronRight size={14} /></>}
              </button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
